import React, { useState, useRef, useEffect, useLayoutEffect } from 'react';
import './DrumRoll.css';

const OPTION_PADDING = 2;

type Option<T> = {
  value: T;
  label: string;
}

type DrumRollProps<T> = {
  options: Option<T>[];
  value: T;
  optionHeight?: number;
  onChange?: (value: T) => void;
}

const DrumRoll: React.FC<DrumRollProps<any>> = <T,>({ options, value, onChange, optionHeight = 25 }: DrumRollProps<T>) => {
  const [container, setContainer] = useState<HTMLDivElement | null>(null);
  const [isScrolling, setIsScrolling] = useState(false);
  const scrollTimeoutRef = useRef<number | null>(null);
  const paramsRef = useRef({ options, value, onChange, optionHeight });
  paramsRef.current = { options, value, onChange, optionHeight };

  const initRef = useRef(false);
  useLayoutEffect(() => {
    if (container == null) return;
    if (initRef.current) return;
    initRef.current = true;
    const { options, value, optionHeight } = paramsRef.current;
    scrollToIndex(options, value, container, optionHeight, true);
  }, [container]);

  useEffect(() => {
    if (container == null) return;
    const { options, value, optionHeight } = paramsRef.current;
    scrollToIndex(options, value, container, optionHeight);
  }, [options, value, container]);

  const handleScroll = () => {
    if (!initRef.current) return;
    if (container == null) return;

    setIsScrolling(true);
    if (scrollTimeoutRef.current !== null) {
      clearTimeout(scrollTimeoutRef.current);
    }

    scrollTimeoutRef.current = window.setTimeout(() => {
      setIsScrolling(false);
    }, 400);
  };

  useEffect(() => {
    if (container == null) return;
    if (isScrolling) return;

    const { options, optionHeight } = paramsRef.current;

    const scrollTop = container.scrollTop;
    const indexesWithDistance = options
      .map((o, i) => ({index: i, distance: Math.abs(scrollTop - optionHeight * (i + OPTION_PADDING))}))
    indexesWithDistance.sort((a, b) => a.distance - b.distance);
    const index = indexesWithDistance[0]?.index ?? 0;

    const value = options.find((_, i) => i === index)?.value;
    if (value != null) {
      if (value !== paramsRef.current.value) {
        paramsRef.current.onChange?.(value);
      } else {
        // 値が変わってない場合はスクロール処理が走らないのでこちらで処理しておく
        scrollToIndex(options, value, container, optionHeight);
      }
    } else {
      console.error(`invalid state, scroll position is out of range: ${scrollTop}`);
    }
  }, [container, isScrolling]);

  const [transforms, setTransforms] = useState(options.map((_, i) => getTransform(container, i, optionHeight)));
  useEffect(() => {
    const listener = () => {
      setTransforms(options.map((_, i) => getTransform(container, i, optionHeight)));
    }
    container?.addEventListener('scroll', listener);
    return () => container?.removeEventListener('scroll', listener);
  }, [container, options, optionHeight]);
  useEffect(() => {
    setTransforms(options.map((_, i) => getTransform(container, i, optionHeight)));
  }, [container, options, optionHeight, value])

  return (
    <div className="drum-roll-container" ref={setContainer} onScroll={handleScroll} style={{'--option-height': `${optionHeight}px`} as any}>
      <ul className="drum-roll-content">
        <li className="drum-roll-padding"></li>
        {options.map((option, i) => (
          <li
            key={String(option.value)}
            className="drum-roll-option"
            onClick={() => onChange?.(option.value)}
          >
            <span className="drum-roll-option-label" style={{'--scale': `${transforms[i]}`} as any}>
              {option.label}
            </span>
          </li>
        ))}
        <li className="drum-roll-padding"></li>
      </ul>
    </div>
  );
};

function scrollToIndex<T>(options: Option<T>[], value: T | undefined, container: HTMLDivElement, optionHeight: number, instant = false) {
  const index = Math.max(options.findIndex(option => option.value === value), 0);
  const scrollTop = optionHeight * (index + OPTION_PADDING);
  if (instant) {
    container.classList.add('instant-scroll');
    container.scrollTo({top: scrollTop, behavior: 'auto'});
    container.classList.remove('instant-scroll');
  } else {
    container.scrollTo({top: scrollTop, behavior: 'smooth'});
  }
}

function getTransform(container: HTMLDivElement | null, index: number, optionHeight: number) {
  if (container == null) return 0;
  const scrollTop =  optionHeight * (index + OPTION_PADDING);
  const direction = scrollTop > container.scrollTop ? 1 : -1;
  const diffTop = Math.abs(container.scrollTop - (scrollTop - optionHeight * 0.5));
  const diffBottom = Math.abs(container.scrollTop - (scrollTop + optionHeight * 0.5));
  const scaleTop = Math.max(0.1, 1 - Math.pow(diffTop / optionHeight, 2) * 0.05);
  const scaleBottom = Math.max(0.1, 1 - Math.pow(diffBottom / optionHeight, 2) * 0.05);
  const scale = direction > 0 ? scaleTop : scaleBottom;
  const angle = (scaleBottom - scaleTop) * 270;
  const translate = (scaleTop - scaleBottom) * optionHeight * 3.5;
  return `scale(${scale}) rotate3d(1, 0, 0, ${angle}deg) translateY(${translate}px)`;
}

export default DrumRoll;
